import React from 'react';
import { Accessibility } from 'lucide-react';
import { useAccessibility } from './AccessibilityProvider';

export function AccessibilityDropdown() {
  const { settings, updateSetting } = useAccessibility();
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  return (
    <div ref={containerRef} className="relative">
        <button
          type="button"
          className="h-10 w-10 rounded-xl border border-blue-200/80 bg-blue-50/85 p-2 text-blue-700 hover:bg-blue-100 dark:border-blue-400/25 dark:bg-blue-500/10 dark:text-blue-200 dark:hover:bg-blue-500/20"
          aria-label="Accessibility settings"
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
        >
          <Accessibility className="w-5 h-5" />
        </button>

      {open && (
        <div className="absolute right-0 mt-2 w-64 rounded-2xl border border-gray-200 bg-white/95 p-1.5 text-popover-foreground shadow-[0_20px_48px_-24px_rgba(15,23,42,0.45)] backdrop-blur-xl dark:border-gray-700 dark:bg-gray-800/95 z-[120]">
          <div className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Accessibility Settings
          </div>

          {[
            { key: 'highContrast', label: 'High Contrast', checked: settings.highContrast },
            { key: 'largeText', label: 'Large Text', checked: settings.largeText },
            { key: 'reduceMotion', label: 'Reduce Motion', checked: settings.reduceMotion },
          ].map((item) => (
            <button
              type="button"
              key={item.key}
              onClick={() => updateSetting(item.key as 'highContrast' | 'largeText' | 'reduceMotion', !item.checked)}
              className="flex w-full items-center justify-between rounded-xl px-2.5 py-2 text-left text-sm transition-colors hover:bg-blue-50 dark:hover:bg-blue-900/20"
            >
              <span>{item.label}</span>
              <span className="text-blue-600 dark:text-blue-300">{item.checked ? '✓' : ''}</span>
            </button>
          ))}

          <div className="my-1 h-px bg-muted" />
          <div className="px-2 py-1.5 text-xs text-slate-500 dark:text-slate-400">
            More options are available in Accessibility page.
          </div>
        </div>
      )}
    </div>
  );
}
